/** Default interval between live portfolio refreshes. */
export const DEFAULT_PORTFOLIO_REFRESH_INTERVAL_SECONDS = 15;

/** Minimum user-selectable portfolio refresh interval. */
export const MINIMUM_PORTFOLIO_REFRESH_INTERVAL_SECONDS = 1;

/** Maximum user-selectable portfolio refresh interval. */
export const MAXIMUM_PORTFOLIO_REFRESH_INTERVAL_SECONDS = 3_600;

/** User-scoped dashboard preferences stored in the browser. */
export interface DashboardPreferences {
  /** Last wallet selected by the authenticated user. */
  walletId: string | null;
  /** Interval between marked portfolio refreshes. */
  portfolioRefreshIntervalSeconds: number;
}

/** Minimal durable browser storage contract used by preference helpers. */
export interface DashboardPreferenceStorage {
  /**
   * Reads a stored value.
   *
   * @param key Storage key.
   * @returns Stored text or null.
   */
  getItem(key: string): string | null;
  /**
   * Persists a value.
   *
   * @param key Storage key.
   * @param value Serialized value.
   * @returns Nothing.
   */
  setItem(key: string, value: string): void;
}

/**
 * Reads validated dashboard preferences for one authenticated user.
 *
 * @param userId Stable authenticated user identifier.
 * @param storage Optional storage override used by tests.
 * @returns Valid preferences with safe defaults.
 */
export function readDashboardPreferences(
  userId: string,
  storage: DashboardPreferenceStorage | null = getBrowserStorage(),
): DashboardPreferences {
  const defaults: DashboardPreferences = {
    walletId: null,
    portfolioRefreshIntervalSeconds:
      DEFAULT_PORTFOLIO_REFRESH_INTERVAL_SECONDS,
  };
  if (storage === null) {
    return defaults;
  }
  try {
    const serialized = storage.getItem(getDashboardPreferenceKey(userId));
    if (serialized === null) {
      return defaults;
    }
    const parsed = JSON.parse(serialized) as {
      walletId?: unknown;
      portfolioRefreshIntervalSeconds?: unknown;
    };
    return {
      walletId: typeof parsed.walletId === "string" ? parsed.walletId : null,
      portfolioRefreshIntervalSeconds: validatePortfolioRefreshIntervalSeconds(
        parsed.portfolioRefreshIntervalSeconds,
      ),
    };
  } catch {
    return defaults;
  }
}

/**
 * Persists validated dashboard preferences for one authenticated user.
 *
 * @param userId Stable authenticated user identifier.
 * @param preferences Preferences to persist.
 * @param storage Optional storage override used by tests.
 * @returns Nothing.
 */
export function writeDashboardPreferences(
  userId: string,
  preferences: DashboardPreferences,
  storage: DashboardPreferenceStorage | null = getBrowserStorage(),
): void {
  if (storage === null) {
    return;
  }
  storage.setItem(
    getDashboardPreferenceKey(userId),
    JSON.stringify({
      walletId: preferences.walletId,
      portfolioRefreshIntervalSeconds: validatePortfolioRefreshIntervalSeconds(
        preferences.portfolioRefreshIntervalSeconds,
      ),
    }),
  );
}

/**
 * Validates a requested portfolio refresh interval.
 *
 * @param value Unknown interval value.
 * @returns A supported whole-second interval or the default.
 */
export function validatePortfolioRefreshIntervalSeconds(
  value: unknown,
): number {
  if (
    typeof value !== "number" ||
    !Number.isFinite(value) ||
    value < MINIMUM_PORTFOLIO_REFRESH_INTERVAL_SECONDS ||
    value > MAXIMUM_PORTFOLIO_REFRESH_INTERVAL_SECONDS
  ) {
    return DEFAULT_PORTFOLIO_REFRESH_INTERVAL_SECONDS;
  }
  return Math.round(value);
}

/**
 * Formats a successful refresh time as a compact relative status.
 *
 * @param updatedAtMilliseconds Successful refresh time.
 * @param nowMilliseconds Current wall-clock time.
 * @returns Human-readable update status.
 */
export function formatUpdatedAgo(
  updatedAtMilliseconds: number | null,
  nowMilliseconds: number,
): string {
  if (updatedAtMilliseconds === null) {
    return "Waiting for first update";
  }
  const elapsedSeconds = Math.max(
    0,
    Math.floor((nowMilliseconds - updatedAtMilliseconds) / 1_000),
  );
  if (elapsedSeconds === 0) {
    return "Updated just now";
  }
  if (elapsedSeconds < 60) {
    return `Updated ${elapsedSeconds} second${elapsedSeconds === 1 ? "" : "s"} ago`;
  }
  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  return `Updated ${elapsedMinutes} minute${elapsedMinutes === 1 ? "" : "s"} ago`;
}

/**
 * Formats a refresh interval for settings copy.
 *
 * @param intervalSeconds Whole-second refresh interval.
 * @returns Compact duration.
 */
export function formatRefreshInterval(intervalSeconds: number): string {
  if (intervalSeconds < 60) {
    return `${intervalSeconds} second${intervalSeconds === 1 ? "" : "s"}`;
  }
  const minutes = intervalSeconds / 60;
  return Number.isInteger(minutes)
    ? `${minutes} minute${minutes === 1 ? "" : "s"}`
    : `${intervalSeconds} seconds`;
}

/**
 * Creates the user-scoped browser preference key.
 *
 * @param userId Stable authenticated user identifier.
 * @returns Storage key.
 */
function getDashboardPreferenceKey(userId: string): string {
  return `papertiger_dashboard_preferences:${userId}`;
}

/**
 * Resolves durable browser storage when running in a browser.
 *
 * @returns Browser storage or null during server-side/test execution.
 */
function getBrowserStorage(): DashboardPreferenceStorage | null {
  return typeof window === "undefined" ? null : window.localStorage;
}
